import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPages.css';

const PrivacyPolicyPage: React.FC = () => {
    return (
        <div className="policy-page">
            <header>
                <Link to="/" className="back-link">← Back to Home</Link>
                <h1>Privacy Policy</h1>
                <p>Last updated: May 27, 2025</p>
            </header>

            <div className="policy-content">
                <section>
                    <h2>Introduction</h2>
                    <p>
                        This Privacy Policy explains how MailMeld ("we", "our", or "us") collects, uses, and protects your information
                        when you use our Google Workspace Add-on. We are committed to ensuring your privacy and protecting your data
                        in accordance with applicable privacy laws including GDPR, CCPA, and other data protection regulations.
                    </p>
                    <p>
                        <strong>By using MailMeld, you agree to the collection and use of information in accordance with this policy.</strong>
                    </p>
                </section>

                <section>
                    <h2>Information We Collect</h2>
                    
                    <h3>Personal Information</h3>
                    <p>When you use MailMeld, we collect the following types of information:</p>
                    <ul>
                        <li><strong>Google Account Information:</strong> Your name, email address, and Google account ID</li>
                        <li><strong>Gmail Data:</strong> Email metadata including subject lines, dates, sender information, and message IDs</li>
                        <li><strong>User Preferences:</strong> Settings for email summary configuration, frequency preferences, and customization options</li>
                        <li><strong>Usage Data:</strong> Information about how you interact with our service, including timestamps and feature usage</li>
                    </ul>
                    
                    <h3>What We Don't Collect</h3>
                    <p><strong>We do not read, store, or process the actual content/body of your emails.</strong> We only access metadata necessary for generating summaries.</p>
                </section>

                <section>
                    <h2>How We Use Your Information</h2>
                    <p>We use your information solely for the following purposes:</p>
                    <ul>
                        <li><strong>Service Provision:</strong> Generate your daily email summaries and provide core functionality</li>
                        <li><strong>Personalization:</strong> Customize your experience based on your preferences and settings</li>
                        <li><strong>Service Improvement:</strong> Analyze usage patterns to improve our features (using aggregated, anonymized data only)</li>
                        <li><strong>Technical Support:</strong> Provide customer support and troubleshoot issues</li>
                        <li><strong>Legal Compliance:</strong> Comply with applicable laws and legal obligations</li>
                    </ul>
                    <p>
                        <strong>We do not use your data for advertising, marketing to third parties, or any purposes not explicitly stated above.</strong>
                    </p>
                </section>

                <section>
                    <h2>Data Sharing and Disclosure</h2>
                    <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following limited circumstances:</p>
                    <ul>
                        <li><strong>Google Workspace Integration:</strong> As necessary to operate within the Google Workspace environment</li>
                        <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
                        <li><strong>Service Providers:</strong> With trusted service providers who assist in our operations under strict confidentiality agreements</li>
                        <li><strong>Business Transfer:</strong> In the event of a merger, acquisition, or sale of assets (users will be notified)</li>
                    </ul>
                </section>

                <section>
                    <h2>Data Security</h2>
                    <p>We implement industry-standard security measures to protect your information:</p>
                    <ul>
                        <li><strong>Encryption:</strong> All data is encrypted in transit using TLS 1.2+ and at rest using AES-256</li>
                        <li><strong>Access Controls:</strong> Strict access controls and authentication for all systems</li>
                        <li><strong>Regular Audits:</strong> Regular security assessments and vulnerability testing</li>
                        <li><strong>Data Minimization:</strong> We collect and retain only the minimum data necessary for our services</li>
                        <li><strong>Google OAuth 2.0:</strong> We use Google's secure OAuth 2.0 for authentication and authorization</li>
                    </ul>
                    <p>
                        While we strive to protect your data, no method of transmission over the Internet is 100% secure. 
                        We cannot guarantee absolute security but commit to promptly addressing any security incidents.
                    </p>
                </section>

                <section>
                    <h2>Data Retention and Deletion</h2>
                    <p>
                        <strong>Retention Period:</strong> We retain your data only as long as necessary to provide our services or as required by law.
                        Specifically:
                    </p>
                    <ul>
                        <li>Email metadata: Retained for up to 90 days for summary generation</li>
                        <li>User preferences: Retained while your account is active</li>
                        <li>Usage analytics: Aggregated data may be retained for up to 2 years</li>
                    </ul>
                    <p>
                        <strong>Deletion:</strong> You can request immediate deletion of your data by contacting us at connect@rvdtechsolutions.com.
                        Upon account deletion, all personal data will be permanently removed within 30 days.
                    </p>
                </section>

                <section>
                    <h2>Your Rights and Choices</h2>
                    <p>You have the following rights regarding your personal information:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of your personal data we hold</li>
                        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                        <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                        <li><strong>Data Portability:</strong> Request export of your data in a machine-readable format</li>
                        <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                        <li><strong>Withdrawal of Consent:</strong> Withdraw consent for data processing at any time</li>
                        <li><strong>Opt-out:</strong> Disconnect the add-on from your Google account at any time</li>
                    </ul>
                    <p>
                        To exercise these rights, contact us at connect@rvdtechsolutions.com. We will respond within 30 days.
                    </p>
                </section>

                <section>
                    <h2>Google API Services User Data Policy Compliance</h2>
                    <p>
                        MailMeld's use of information received from Gmail APIs adheres to the 
                        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                            Google API Services User Data Policy
                        </a>, including the Limited Use requirements.
                    </p>
                    <p>Specifically:</p>
                    <ul>
                        <li>We only request the minimum scopes necessary for our functionality</li>
                        <li>Gmail data is used solely for providing email summary services</li>
                        <li>We do not transfer Gmail data to third parties</li>
                        <li>We do not use Gmail data for advertising or commercial purposes beyond our core service</li>
                    </ul>
                </section>

                <section>
                    <h2>International Data Transfers</h2>
                    <p>
                        Your data may be processed in countries outside your residence. We ensure adequate protection 
                        through appropriate safeguards such as Standard Contractual Clauses or adequacy decisions.
                    </p>
                </section>

                <section>
                    <h2>Children's Privacy</h2>
                    <p>
                        Our service is not intended for children under 13 years of age. We do not knowingly collect 
                        personal information from children under 13. If we discover such data has been collected, 
                        we will delete it immediately.
                    </p>
                </section>

                <section>
                    <h2>Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
                        Significant changes will be communicated through:
                    </p>
                    <ul>
                        <li>Email notification to your registered address</li>
                        <li>In-app notifications</li>
                        <li>Prominent notice on our website</li>
                    </ul>
                    <p>
                        Continued use of our service after changes constitutes acceptance of the updated policy.
                    </p>
                </section>

                <section>
                    <h2>Contact Information</h2>
                    <p>
                        For any questions, concerns, or requests regarding this Privacy Policy or your data, please contact us:
                    </p>
                    <div style={{ marginLeft: '20px' }}>
                        <p><strong>RVD Tech Solutions</strong></p>
                        <p>Email: connect@rvdtechsolutions.com</p>
                        <p>Response Time: We aim to respond within 48 hours</p>
                    </div>
                    <p>
                        If you are not satisfied with our response, you have the right to lodge a complaint with 
                        your local data protection authority.
                    </p>
                </section>

                <section>
                    <h2>Effective Date</h2>
                    <p>
                        This Privacy Policy is effective as of May 27, 2025, and applies to all information collected 
                        by MailMeld from that date forward.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;